import { useState, useCallback } from 'react'
import { Navigate } from 'react-router-dom'
import { format } from 'date-fns'
import { useAuth } from '@/hooks/use-auth'
import { useProject } from '@/hooks/use-project'
import { api, type ReportData, type ReportLinkData } from '@/lib/api'
import { ReportChart } from '@/components/report-chart'
import { DatePicker } from '@/components/date-picker'
import { formatDateRange, getLastSunday, getYesterday } from '@/lib/utils'
import { buildAppHref } from '@/lib/auth'
import { FileSpreadsheet, Download, Eye, Loader2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'

type ReportType = 'weekly' | 'daily' | 'range'

function toDateStr(date: Date | undefined): string {
  return date ? format(date, 'yyyy-MM-dd') : ''
}

function parseDate(str: string): Date | undefined {
  if (!str) return undefined
  const d = new Date(str + 'T00:00:00')
  return isNaN(d.getTime()) ? undefined : d
}

export function ReportsPage() {
  const auth = useAuth()
  const project = useProject()

  const [reportType, setReportType] = useState<ReportType>('weekly')
  const [startDate, setStartDate] = useState<Date | undefined>(parseDate(getLastSunday()))
  const [endDate, setEndDate] = useState<Date | undefined>(undefined)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const [reportData, setReportData] = useState<ReportData | null>(null)
  const [selectedLinkIdx, setSelectedLinkIdx] = useState(0)
  const [loading, setLoading] = useState(false)
  const [reportParams, setReportParams] = useState<{ project: string; date: string; type: string; end_date: string | null } | null>(null)

  const showError = useCallback((msg: string) => {
    setErrorMsg(msg)
    setTimeout(() => setErrorMsg(null), 5000)
  }, [])

  if (!project) {
    return (
      <Navigate
        to={auth?.mode === 'admin' ? buildAppHref('/admin', { mode: 'admin', projectCode: null }) : '/'}
        replace
      />
    )
  }

  const apiType = reportType === 'daily' ? '1' : reportType === 'weekly' ? '2' : '3'
  const startDateStr = toDateStr(startDate)
  const endDateStr = toDateStr(endDate)

  function handleTypeChange(type: ReportType) {
    setReportType(type)
    if (type === 'weekly') {
      setStartDate(parseDate(getLastSunday()))
    } else if (type === 'daily') {
      setStartDate(parseDate(getYesterday()))
    }
    if (type !== 'range') setEndDate(undefined)
  }

  async function handleDownload() {
    if (!startDateStr) { showError('Please select a start date'); return }
    if (reportType === 'range' && !endDateStr) { showError('Please select an end date'); return }
    try {
      await api.downloadReport(project!, startDateStr, apiType, reportType === 'range' ? endDateStr : null)
    } catch (err) {
      showError(`Error generating report: ${(err as Error).message}`)
    }
  }

  async function handleShowReport() {
    if (!startDateStr) { showError('Please select a start date'); return }
    if (reportType === 'range' && !endDateStr) { showError('Please select an end date'); return }
    setLoading(true)
    try {
      const data = await api.getReportData(project!, startDateStr, apiType, reportType === 'range' ? endDateStr : null)
      setReportData(data)
      setReportParams({ project: project!, date: startDateStr, type: apiType, end_date: reportType === 'range' ? endDateStr : null })
      setSelectedLinkIdx(0)
    } catch (err) {
      showError(`Error loading report: ${(err as Error).message}`)
    } finally {
      setLoading(false)
    }
  }

  const dateSummary = formatDateRange(startDateStr, endDateStr || null, reportType)
  const selectedLink: ReportLinkData | undefined = reportData?.links[selectedLinkIdx]

  return (
    <div className="mx-auto max-w-[1200px] px-8 py-8 flex flex-col gap-6">
      <Card className="bg-card/95 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-3xl">
            <FileSpreadsheet className="h-8 w-8" /> Generate Reports
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center">
            <Card className="w-full max-w-[600px]">
              <CardContent className="pt-6 flex flex-col gap-6">
                {/* Report type selector */}
                <div className="flex flex-col gap-2">
                  <Label className="font-semibold">Report Type:</Label>
                  <div className="flex gap-2">
                    {(['weekly', 'daily', 'range'] as const).map((type) => (
                      <Button
                        key={type}
                        variant={reportType === type ? 'default' : 'outline'}
                        className="flex-1 capitalize"
                        onClick={() => handleTypeChange(type)}
                      >
                        {type}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Date pickers */}
                <div className="flex flex-col gap-2">
                  <Label className="font-semibold">Date Selection:</Label>
                  <div className={`flex gap-6 ${reportType === 'range' ? 'flex-row' : 'flex-col'}`}>
                    <div className="flex flex-col gap-1.5 flex-1">
                      <Label className="text-sm">Start Date:</Label>
                      <DatePicker value={startDate} onChange={setStartDate} />
                    </div>
                    {reportType === 'range' && (
                      <div className="flex flex-col gap-1.5 flex-1">
                        <Label className="text-sm">End Date:</Label>
                        <DatePicker
                          value={endDate}
                          onChange={setEndDate}
                          disabled={(date) => !!startDate && date < startDate}
                          placeholder="Pick end date"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Date summary */}
                {dateSummary && (
                  <div className="rounded-md border-l-4 border-primary bg-muted p-3 text-sm font-medium">
                    {dateSummary}
                  </div>
                )}

                {/* Buttons */}
                <div className="flex gap-3">
                  <Button variant="secondary" onClick={handleShowReport} disabled={loading}>
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                    Show Report
                  </Button>
                  <Button onClick={handleDownload}>
                    <Download className="h-4 w-4" /> Download Report
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {errorMsg && (
            <div className="mt-4 rounded-lg border-l-4 border-destructive bg-destructive/10 p-4 text-sm text-destructive">
              {errorMsg}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Report results card (replaces modal) */}
      {reportData && selectedLink && (
        <Card className="bg-card/95 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <CardTitle className="text-xl">
                {reportParams?.type === '1' ? 'Daily' : reportParams?.type === '2' ? 'Weekly' : 'Range'} Report — {reportParams?.project} — {reportParams?.date}
              </CardTitle>
              <Button
                size="sm"
                onClick={async () => {
                  if (reportParams) await api.downloadReport(reportParams.project, reportParams.date, reportParams.type, reportParams.end_date)
                }}
              >
                <Download className="h-4 w-4" /> Download Report
              </Button>
            </div>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {/* Link selector */}
            <div className="flex flex-col gap-1.5 pb-4 border-b">
              <Label className="font-semibold">Select Link:</Label>
              <select
                value={selectedLinkIdx}
                onChange={(e) => setSelectedLinkIdx(Number(e.target.value))}
                className="flex h-9 w-full max-w-[400px] rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                {reportData.links.map((link, i) => (
                  <option key={i} value={i}>{link.link_name}</option>
                ))}
              </select>
            </div>

            {/* Content grid */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className="overflow-auto rounded-md border max-h-[60vh]">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-primary hover:bg-primary">
                      <TableHead className="text-primary-foreground font-semibold sticky top-0">
                        {reportData.type === 'daily' ? 'Time' : 'Day / Time'}
                      </TableHead>
                      <TableHead className="text-primary-foreground font-semibold sticky top-0">Baseline</TableHead>
                      <TableHead className="text-primary-foreground font-semibold sticky top-0">Actual</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedLink.data.map((item, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-sm">
                          {reportData.type === 'daily'
                            ? (item.time || '')
                            : (item.day ? `${item.day} ${item.time || ''}` : item.time || '')}
                        </TableCell>
                        <TableCell className="text-sm">{item.baseline || 0}</TableCell>
                        <TableCell className="text-sm">{item.actual || 0}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="rounded-md border bg-muted p-4 flex items-center justify-center overflow-hidden min-h-[300px]">
                <ReportChart linkData={selectedLink} reportType={reportData.type} />
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
